import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { createChewyApiClient } from '@chewy/loyalty-graphql-client';
import { createContext, ReactNode, useContext, useMemo } from 'react';

interface GraphQLClients {
  widgetsClient: ApolloClient<NormalizedCacheObject>;
  apiRouterClient: ApolloClient<NormalizedCacheObject>;
}

const CHEWY_API_ROUTER_URI = '/api/api-router/graphql';
const CHEWY_WIDGETS_URI = '/api/widgets/graphql';

const GraphQLClientsContext = createContext<GraphQLClients | null>(null);

interface GraphQLClientsProviderProps {
  children: ReactNode;
}

export const GraphQLClientsProvider = ({ children }: GraphQLClientsProviderProps) => {
  const clients = useMemo(() => {
    const widgetsClient = createChewyApiClient({
      uri: CHEWY_WIDGETS_URI,
    });

    const apiRouterClient = createChewyApiClient({
      uri: CHEWY_API_ROUTER_URI,
      headers: {
        'x-chewy-component-id': 'loyalty-ui',
      },
    });

    return { widgetsClient, apiRouterClient };
  }, []);

  return (
    <GraphQLClientsContext.Provider value={clients}>
      <ApolloProvider client={clients.apiRouterClient}>{children}</ApolloProvider>
    </GraphQLClientsContext.Provider>
  );
};

export const useGraphQLClients = (): GraphQLClients => {
  const clients = useContext(GraphQLClientsContext);
  if (!clients) {
    throw new Error('useGraphQLClients must be used within GraphQLClientsProvider');
  }
  return clients;
};

export const useWidgetsClient = () => useGraphQLClients().widgetsClient;
export const useApiRouterClient = () => useGraphQLClients().apiRouterClient;
